import { Preferences } from '@capacitor/preferences'
import { Capacitor } from '@capacitor/core'
import { BackgroundFetch } from '@transistorsoft/capacitor-background-fetch'
import type { Store } from 'pinia'
import DatabaseManager from './DatabaseManager'
import { Persistence } from '../entities/Persistence'
import { report } from './ExceptionReportingManager'

type SyncableStore = Store & { sync: () => void }
type HydratableStore = Store & { hydrate: (s: string) => void }

export default class StoreManager {
  static keepPersisted(stores: Array<Store>): void {
    for (const store of stores) {
      store.$subscribe(() => {
        StoreManager.persist(store).catch((error) => {
          console.error('Error persisting store:', error)
        })
      })
    }
  }
  public static async persist(store: Store) {
    console.log('Persisting store:', store.$id)
    if (!DatabaseManager.appDataSource) {
      console.warn('Database not initialized, falling back to Preferences API')
      await Preferences.set({
        key: store.$id,
        value: JSON.stringify(store.$state),
      })
      return
    }

    try {
      const persistenceRepo =
        DatabaseManager.appDataSource.getRepository(Persistence)
      const existingRecord = await persistenceRepo.findOne({
        where: { key: store.$id },
      })

      const value = JSON.stringify(store.$state)

      if (existingRecord) {
        existingRecord.value = value
        await persistenceRepo.save(existingRecord)
      } else {
        const newRecord = new Persistence()
        newRecord.key = store.$id
        newRecord.value = value
        await persistenceRepo.upsert(newRecord, ['key'])
      }
    } catch (error) {
      console.error(
        'Failed to persist store to SQLite, falling back to Preferences API:',
        error,
      )
      report(error as Error)
      await Preferences.set({
        key: store.$id,
        value: JSON.stringify(store.$state),
      })
    }
  }

  static async hydrate(stores: Array<HydratableStore>): Promise<void> {
    const promises = stores.map(async (store) => {
      let value: string | null = null

      // Try to get from SQLite first
      if (DatabaseManager.appDataSource) {
        try {
          const persistenceRepo =
            DatabaseManager.appDataSource.getRepository(Persistence)
          const record = await persistenceRepo.findOne({
            where: { key: store.$id },
          })
          if (record) {
            value = record.value
            console.log('hydrating from SQLite:', store.$id)
          }
        } catch (error) {
          console.error('Failed to hydrate from SQLite:', error)
        }
      }

      // Fallback to Preferences API if SQLite failed or no record found
      if (!value) {
        const data = await Preferences.get({ key: store.$id })
        value = data.value
        console.log('value from Preferences:', value)
        console.log('hydrating from Preferences:', store.$id)
      }

      if (value) store.hydrate(value)
    })
    await Promise.all(promises)
  }

  static async sync(stores: Array<SyncableStore>) {
    const promises = []
    for (const store of stores) {
      console.log('syncing ', store.$id)
      promises.push(store.sync())
    }
    return Promise.all(promises)
  }

  static async registerForBackgroundSync(stores: Array<SyncableStore>) {
    if (Capacitor.getPlatform() === 'web') return
    const onBackgroundSync = async () => {
      for (const store of stores) {
        console.log('syncing ', store.$id)
        await store.sync()
      }
    }
    const status = await BackgroundFetch.configure(
      {
        minimumFetchInterval: 15,
      },
      async (taskId) => {
        console.log('BackgroundSync started')
        await onBackgroundSync()
        console.log('BackgroundSync complete')
        void BackgroundFetch.finish(taskId)
      },
      (taskId) => {
        console.log('BackgroundSync was forced to finish early')
        // place to do any cleanup
        void BackgroundFetch.finish(taskId)
      },
    )

    if (status !== BackgroundFetch.STATUS_AVAILABLE) {
      if (status === BackgroundFetch.STATUS_DENIED) {
        console.error(
          'The user explicitly disabled background behavior for this app or for the whole system.',
        )
      } else if (status === BackgroundFetch.STATUS_RESTRICTED) {
        console.error(
          'Background updates are unavailable and the user cannot enable them again.',
        )
      }
    }
  }

  static clear(stores: Array<Store>) {
    for (const store of stores) {
      console.log('clearing ', store.$id)
      store.$reset()
    }
  }

  static async migrateFromPreferencesToSQLite(
    stores: Array<Store>,
  ): Promise<void> {
    if (!DatabaseManager.appDataSource) {
      console.warn(
        'Database not initialized, cannot migrate from Preferences to SQLite',
      )
      return
    }

    console.log('Starting migration from Preferences API to SQLite')
    const persistenceRepo =
      DatabaseManager.appDataSource.getRepository(Persistence)

    for (const store of stores) {
      try {
        // Check if data already exists in SQLite
        const existingRecord = await persistenceRepo.findOne({
          where: { key: store.$id },
        })
        if (existingRecord) {
          console.log(
            `Store ${store.$id} already exists in SQLite, skipping migration`,
          )
          continue
        }

        // Get data from Preferences API
        const preferencesData = await Preferences.get({ key: store.$id })
        if (!preferencesData.value) {
          console.log(`No data found in Preferences for store ${store.$id}`)
          continue
        }

        // Migrate to SQLite
        const newRecord = new Persistence()
        newRecord.key = store.$id
        newRecord.value = preferencesData.value
        await persistenceRepo.upsert(newRecord, ['key'])

        console.log(
          `Successfully migrated store ${store.$id} from Preferences to SQLite`,
        )

        await Preferences.remove({ key: store.$id })
      } catch (error) {
        report(error as Error)
      }
    }

    console.log('Migration from Preferences API to SQLite completed')
  }
}
